// Sales sekce (30. 9. 2026, schválený mockup v8): hlavička, záložky Plán (dřív Dnes), Kontakty, Zkušební.
// Layout drží data fronty Dnes (odznak u záložky) a hlídá konec fronty: když paní vyřídí
// poslední kontakt a běží měření, zeptá se, jestli ho ukončit.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { PhoneCall, UserPlus, RefreshCw, Settings, Clock, Square } from "lucide-react";
import GradientHeader from "../../components/GradientHeader.jsx";
import { Stranka, Zalozky, Modal, HlavickaOkna } from "../../components/ui.jsx";
import { getCurrentUser } from "../../lib/auth.js";
import { toast } from "../../components/Toast.jsx";
import { salesGet, stavMereni, zastavMereni } from "../../components/sales/salesApi.js";
import { NovyKontaktDialog } from "../../components/sales/Dialogy.jsx";
import { Oznameni } from "../../components/sales/salesUi.jsx";
import NastaveniSales from "../../components/sales/NastaveniSales.jsx";

function Odznak({ n }) {
  if (!n) return null;
  return <span className="ml-1.5 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-brand-gold px-1.5 text-xs font-bold tabular-nums text-brand-dark">{n}</span>;
}
function delka(sec) { const s = Math.max(0, Math.floor(sec)); return `${Math.floor(s / 3600)}:${String(Math.floor((s % 3600) / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`; }

export default function SalesLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const role = String(getCurrentUser()?.role || "").toLowerCase();
  const [dnes, setDnes] = useState(null);
  const [chyba, setChyba] = useState("");
  const [novy, setNovy] = useState(false);
  const [nastaveni, setNastaveni] = useState(false);
  const [konec, setKonec] = useState(null); // { start_at } běžícího měření
  const vyrizeno = useRef(0);

  const nactiDnes = useCallback(async () => {
    try {
      const d = await salesGet("dnes"); setDnes(d); setChyba("");
      // Počet k zavolání pro odznak u Volání v postranním menu (Sidebar).
      window.dispatchEvent(new CustomEvent("fw40-sales-pocet", { detail: d?.pocet || 0 }));
      return d;
    }
    catch (e) { setChyba(e.message); return null; }
  }, []);
  useEffect(() => { nactiDnes(); }, [nactiDnes]);

  // Po každé akci nad kontaktem: znovu načíst frontu. Když se vyprázdnila, nabídnout konec měření.
  const poZmene = useCallback(async () => {
    const pred = dnes?.pocet ?? 0;
    vyrizeno.current += 1;
    const d = await nactiDnes();
    if (d && d.pocet === 0 && pred > 0 && vyrizeno.current > 0) {
      const bezi = await stavMereni();
      if (bezi && /sales/i.test(String(bezi.oblast || ""))) setKonec(bezi);
    }
  }, [dnes, nactiDnes]);

  const ukoncit = async () => {
    const ok = await zastavMereni();
    setKonec(null);
    toast(ok ? "Měření ukončeno a uloženo." : "Měření se nepodařilo ukončit, zkus to ve stopkách nahoře.", ok ? "success" : "error");
  };

  const zalozky = [
    { key: "/sales/dnes", label: <span className="inline-flex items-center">Plán<Odznak n={dnes?.pocet} /></span> },
    { key: "/sales/kontakty", label: "Kontakty" },
    { key: "/sales/zkusebni", label: "Zkušební" },
  ];
  const aktivni = zalozky.find((z) => location.pathname.startsWith(z.key))?.key || "";
  const akce = [
    { klic: "novy", ikona: UserPlus, popis: "Nový kontakt", onClick: () => setNovy(true) },
    { klic: "obnovit", ikona: RefreshCw, popis: "Obnovit", onClick: () => { nactiDnes(); window.dispatchEvent(new Event("fw40-sales-obnovit")); } },
    ...(role === "owner" || role === "manager" ? [{ klic: "nastaveni", ikona: Settings, popis: "Nastavení sales", onClick: () => setNastaveni(true) }] : []),
  ];

  return (
    <Stranka>
      <GradientHeader title="Sales" icon={PhoneCall} akce={akce}
        napoveda="Plán ukazuje, komu volat dnes, tento týden i měsíc. U jména klepni na šipku, otevře se karta kontaktu. Po hovoru klepni, jak dopadl, a appka sama pošle SMS, e‑maily, fakturu i zprávu trenérovi." />
      <Zalozky items={zalozky} value={aktivni} onChange={(k) => navigate(k)} aria-label="Sekce sales" stitek="Sales" />
      {chyba && <Oznameni ton="problem">{chyba}</Oznameni>}
      {dnes && !dnes.zapnuto && (role === "owner" || role === "manager") && (
        <Oznameni ton="pozor">Sales je v nastavení vypnuté: ranní SMS, připomínky a pondělní report se zatím neposílají. Ruční akce (SMS po nezvednutí, domluvení zkušebního) fungují vždy.</Oznameni>
      )}
      <Outlet context={{ dnes, nactiDnes, poZmene }} />

      <NovyKontaktDialog open={novy} onClose={() => setNovy(false)} onHotovo={(r) => { setNovy(false); nactiDnes(); navigate(`/sales/kontakt/${r.lead_id}`); }} />
      {nastaveni && <NastaveniSales open onClose={() => { setNastaveni(false); nactiDnes(); }} />}
      <Modal open={!!konec} onClose={() => setKonec(null)} size="sm" plnaHlavicka
        header={<HlavickaOkna ikona={Clock} nazev="Hotovo na dnes" popis="Poslední kontakt vyřízený" onClose={() => setKonec(null)} />}
        footer={<>
          <button type="button" className="btn-secondary" onClick={() => setKonec(null)}>Ještě ne</button>
          <button type="button" className="btn-primary" onClick={ukoncit}><Square className="h-4 w-4" />Ukončit měření</button>
        </>}>
        {konec && (
          <div className="space-y-3">
            <Oznameni ton="ok"><b>Dnešní fronta je hotová.</b> Všechny kontakty mají zapsaný výsledek.</Oznameni>
            <div className="text-center">
              <div className="text-[13px] font-semibold uppercase tracking-wide text-slate-500">Měříš {konec.oblast || "Sales"}</div>
              <div className="font-serif text-4xl font-bold tabular-nums text-slate-900">{delka((Date.now() - new Date(konec.start_at).getTime()) / 1000)}</div>
            </div>
          </div>
        )}
      </Modal>
    </Stranka>
  );
}
